import type { GuideCommand } from "../../../api/textGuide";

export interface Stage { id: string; name: string; short: string }
export interface JourneyCommand extends GuideCommand { stage: Stage; order: number; title: string; keywords: string[]; slug: string }

export const STAGES: Stage[] = [
  { id: "01", name: "Before Your Shift", short: "Before" },
  { id: "02", name: "Starting Your Shift", short: "Start" },
  { id: "03", name: "During Your Shift", short: "During" },
  { id: "04", name: "Ending Your Shift", short: "End" },
  { id: "05", name: "Time & Corrections", short: "Time" },
  { id: "06", name: "Additional Commands", short: "More" },
];

/** Journey placement for each code from the guide (GET /api/public/text-guide); everything else comes from that source. */
const META: Record<string, [stage: string, order: number, title: string, keywords: string[]]> = {
  SHIFTS: ["01", 1, "See your upcoming shifts", ["schedule", "list", "when", "next"]],
  STATUS: ["01", 2, "Check your status", ["clocked", "current", "next shift"]],
  CONFIRM: ["01", 3, "Accept offered shifts", ["accept", "yes", "offer", "all"]],
  DECLINE: ["01", 4, "Turn down a shift", ["reject", "no", "offer", "cant make it"]],
  LATE: ["02", 5, "Running late", ["delay", "traffic", "minutes", "crew lead"]],
  IN: ["02", 6, "Clock in", ["arrive", "start", "punch", "gps", "location"]],
  BREAK: ["03", 7, "Start a break", ["meal", "rest", "lunch", "pause"]],
  BACK: ["03", 8, "End your break", ["return", "resume", "meal"]],
  OUT: ["04", 9, "Clock out", ["leave", "finish", "punch", "gps", "location"]],
  HOURS: ["05", 10, "Hours this week", ["time", "worked", "week", "total"]],
  FIX: ["05", 11, "Fix a punch", ["correction", "wrong", "missed", "mistake"]],
  HELP: ["06", 12, "Get help", ["support", "contact", "info"]],
  STOP: ["06", 13, "Stop texts", ["opt out", "unsubscribe", "cancel"]],
  START: ["06", 14, "Restart texts", ["opt in", "resume", "unstop"]],
};

export function buildJourney(cmds: GuideCommand[]): JourneyCommand[] {
  const stageOf = (id: string) => STAGES.find((s) => s.id === id) ?? STAGES[STAGES.length - 1];
  return cmds
    .map((c, i) => {
      const [stage, order, title, keywords] = META[c.code] ?? ["06", 100 + i, c.code, []];
      return { ...c, stage: stageOf(stage), order, title, keywords, slug: c.code.toLowerCase() };
    })
    .sort((a, b) => a.order - b.order);
}

export const pad = (n: number) => String(n).padStart(2, "0");

export function matches(c: JourneyCommand, q: string): boolean {
  const s = q.trim().toLowerCase();
  if (!s) return true;
  return [c.code, c.title, c.does, c.window, c.example, ...c.forms, ...c.synonyms, ...c.keywords].some((x) => x.toLowerCase().includes(s));
}
